import { useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import ClientForm, { getClientError, useClientState } from '@/components/ClientForm';
import Box from '@/design_system/Box';
import FoldableSection from '@/design_system/FoldableSection';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { formatStreet, formatZip } from '@/models/address';
import { formatPhone } from '@/models/partials/phone';
import {
  RequestWithRelations,
  useUpdateDraftRequest,
  useUpdateRequestClient,
} from '@/models/request';
import { useStores } from '@/models/store';
import { getUserStoresWithPermission } from '@/models/user';
import { useCurrentSession } from '@/services/auth';
import { isEmailValid } from '@/utils/email';

import { RequestContextData } from '../../contexts/interfaces';
import { useRequestContext } from '../../contexts/RequestContext';

import { ClientManager } from './ClientManager/ClientManager';

import './ClientStoreForm.css';

const ClientStoreForm = ({ showErrors }: { showErrors: boolean }) => {
  const { request, view } = useRequestContext();

  const hasError = getClientStoreError(request, view.client);

  return (
    <FoldableSection
      label={
        <Title as="h2">
          {request.client ? (
            <>
              <Trans id="requests.new.client.title">Client information</Trans>
              {!view.client.required && <OptionalLabel />}
            </>
          ) : (
            <Trans id="requests.new.store.title">Store information</Trans>
          )}
        </Title>
      }
      startClosed={!!request.client && !view.client.required}
      isInvalid={hasError && showErrors}
    >
      <Box padding="16px" gap="1rem" style={{ flex: 'initial' }} className="client-store-form">
        {!!request.client && <ClientFormWrapper showErrors={showErrors} />}
        {!request.client && <StoreFormWrapper showErrors={showErrors} />}
      </Box>
    </FoldableSection>
  );
};

const ClientFormWrapper = ({ showErrors }: { showErrors: boolean }) => {
  const { request, workflow } = useRequestContext();
  const { mutateAsync: updateRequestClient } = useUpdateRequestClient(request.id);

  const debouncedUpdateRequestClient = useMemo(() => {
    return debounce(updateRequestClient, 500);
  }, [updateRequestClient]);

  const clientState = useClientState((data) => {
    debouncedUpdateRequestClient({
      client: {
        name: data.name || null, // The field "name" starts as an empty string, that's why we use the operator "||"
        title: data.title,
        firstName: data.firstName || null, // The field "firstName" starts as an empty string, that's why we use the operator "||"
        lastName: data.lastName || null, // The field "lastName" starts as an empty string, that's why we use the operator "||"
        email: isEmailValid(data.email) ? data.email : null,
        address: data.address ?? undefined,
        billingAddress: data.billingAddress ?? undefined,
        phone: data.phone,
        deliveryOption: data.deliveryOption,
        sendcloudServicePointId: data.sendcloudServicePointId,
        locale: data.locale,
      },
      vip: data.vip,
    });
  });

  const isClientManagerEnabled = workflow?.config.clientManagement?.enabled;

  if (isClientManagerEnabled) {
    return <ClientManager />;
  }

  return (
    <ClientForm state={clientState} hideDeliveryOption={!request.storeId} showErrors={showErrors} />
  );
};

const StoreFormWrapper = ({ showErrors }: { showErrors: boolean }) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const { request } = useRequestContext();

  const currentSessionStores = getUserStoresWithPermission(
    currentSession,
    'create_store_request',
    request.organizationId
  );

  const { data: { stores: allStores } = { stores: [] } } = useStores(
    { limit: 100, internal: true, embedded: true },
    { enabled: !currentSessionStores?.length }
  );

  const stores = currentSessionStores?.length ? currentSessionStores : allStores;
  const storeOptions = stores.map((store) => ({ id: store.id, text: store.name }));

  const { mutateAsync: updateDraftRequest } = useUpdateDraftRequest(request.id);

  const showSelect = !currentSessionStores?.length && request.draft;

  return (
    <>
      {showSelect && (
        <InputSelect
          variant="select"
          label={t({ id: 'requests.new.store-select.label', message: 'Store' })}
          placeholder={t({
            id: 'requests.new.store-select.placeholder',
            message: 'Select a store',
          })}
          isSearchable={false}
          value={storeOptions.find((storeOption) => storeOption.id === request.storeId)}
          options={storeOptions}
          getOptionValue={(storeOption) => storeOption.id}
          getOptionLabel={(storeOption) => storeOption.text}
          onChange={(storeOption) => {
            if (!!storeOption && storeOption.id !== request.storeId) {
              updateDraftRequest({ storeId: storeOption.id });
            }
          }}
          error={
            showErrors && !request.storeId
              ? t({
                  id: 'requests.client-store-form.error.no-store',
                  message: 'Please select a store',
                })
              : undefined
          }
          style={{ flex: 1 }}
        />
      )}
      {request.store &&
        (showSelect ? (
          <Box padding="16px" gap="1rem">
            <StoreInfo store={request.store} />
          </Box>
        ) : (
          <StoreInfo store={request.store} />
        ))}
    </>
  );
};

const StoreInfo = ({ store }: { store: Exclude<RequestWithRelations['store'], null> }) => {
  return (
    <Stack gap="0.25rem">
      <p className="label-100-medium">{store.name}</p>
      {!!store.address && (
        <Paragraph>
          {formatStreet(store.address)}
          <br />
          {formatZip(store.address)}
          <br />
          {formatPhone(store.contactInfo?.phone)}
        </Paragraph>
      )}
    </Stack>
  );
};

export default ClientStoreForm;

export const getClientStoreError = (
  request: RequestWithRelations,
  view: RequestContextData['view']['client']
) => {
  return request.client
    ? getClientError({
        client: request.client,
        view,
      }).hasError
    : !request.storeId;
};
